import { useContext, useEffect, useMemo, useRef } from 'react';

import { InspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto as AccessibleAccount } from 'shared/models/payments/inspiredPaymentPortalAPIAccessibleAccountsAccessibleAccountDto';
import { removeObservableUserHeader, setObservableUserHeader } from 'shared/utils/apiService';
import { useFilterSessionStorage } from 'shared/components/Filter/hooks';

import AccessibleAccountsContext from './AccessibleAccounts.context';

export const useAccessibleAccounts = () => useContext(AccessibleAccountsContext);

export const useObservableUserHeader = (
  isCurrentAccountObserver: boolean,
  currentAccount: AccessibleAccount | null
) => {
  useEffect(() => {
    if (isCurrentAccountObserver && currentAccount?.accountBillingCode) {
      setObservableUserHeader(currentAccount.accountBillingCode);
    } else {
      removeObservableUserHeader();
    }
  }, [isCurrentAccountObserver, currentAccount?.accountBillingCode]);
};

export const useGetInitialAccount = (accessibleAccounts: AccessibleAccount[]) => {
  return useMemo(() => {
    const loggedAccount = accessibleAccounts.find(account => account.isCurrentUser) || null;
    const isLoggedAccountObserver = !loggedAccount?.accountBillingCode;

    if (!isLoggedAccountObserver) {
      return loggedAccount;
    }

    return accessibleAccounts.find(account => account.accountBillingCode) || null;
  }, [accessibleAccounts]);
};

export const useSetDefaultAcademicYear = (defaultAcademicYear: string) => {
  const { storedValue, setStoredValue } = useFilterSessionStorage();
  const storedValueRef = useRef(storedValue);

  storedValueRef.current = storedValue;

  useEffect(() => {
    const currentStoredValue = storedValueRef.current;

    if (!currentStoredValue?.academicYear && defaultAcademicYear) {
      setStoredValue({ ...currentStoredValue, academicYear: defaultAcademicYear });
    }
  }, [defaultAcademicYear, setStoredValue]);
};
